import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { reportStyle } from '../../../shared/ReportShell';
import { AvailabilityPill } from '../../../shared/Pill';
import { useAnchorPriceGroupName } from '../../../shared/useAnchorPriceGroupName';
import { todayInTenant } from '../../../shared/tenantClock';
import { formatMoney as formatMoneyShared, formatNumber } from '../../../shared/numberFormat';

const MANUAL_PRICE_TITLE = "Manually overridden from the conversion rate's calculated price";

// Small filled-circle "i" — same visual language as ProductForm's revert
// button — rather than a bare `*`, which is too small a hover target on its
// own. Shared by the Report and the product list so the marker (and its
// footnote below) look identical everywhere it appears.
export function ManualPriceIcon() {
  return (
    <span
      title={MANUAL_PRICE_TITLE}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 14,
        height: 14,
        borderRadius: '50%',
        backgroundColor: '#1976d2',
        color: '#fff',
        fontSize: 10,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      i
    </span>
  );
}

// '—' for blank, otherwise shared/numberFormat.js's company style.
export function formatMoney(value) {
  return formatMoneyShared(value) || '—';
}

export function formatCost(product) {
  if (product.supplier && product.cost_price_foreign !== null && product.cost_price_foreign !== undefined) {
    const foreign = Number(product.cost_price_foreign);
    const aud = foreign * Number(product.supplier.exchange_rate);
    return `${formatMoneyShared(foreign, { currency: product.supplier.currency_code })} (${formatMoney(aud)})`;
  }
  return formatMoney(product.cost_price);
}

function toDate(dateOnlyString) {
  return new Date(`${dateOnlyString}T00:00:00Z`);
}

function todayDateOnly() {
  return todayInTenant();
}

// Mirrors uomPriceScheduleService.js on the backend — the schedule entry
// whose [effective_from, effective_to] range covers asOfDate (blank
// effective_to = open-ended). Price is now a dated schedule per UoM row
// (issue #95), not a single mutable value, so "the price" always means
// "whichever entry is in effect as of a given date" — today, for display.
export function getCurrentPriceEntry(scheduleEntries, asOfDateOnlyString = todayDateOnly()) {
  const asOf = toDate(asOfDateOnlyString);
  return (scheduleEntries || []).find((entry) => {
    if (toDate(entry.effective_from) > asOf) return false;
    if (entry.effective_to && toDate(entry.effective_to) < asOf) return false;
    return true;
  }) || null;
}

export function getCurrentSchedulePrice(scheduleEntries, asOfDateOnlyString = todayDateOnly()) {
  const entry = getCurrentPriceEntry(scheduleEntries, asOfDateOnlyString);
  return entry ? Number(entry.price) : null;
}

// Base Price is no longer a scalar on the product (story #75), nor a flat
// value on its base UoM row (issue #95 — it's a dated schedule now) — it's
// whatever price is currently in effect on the product's own base UoM row.
export function getBasePrice(product) {
  const uomRows = product.uoms || [];
  const baseRow = uomRows.find((row) => String(row.uom_id) === String(product.base_uom_id));
  return baseRow ? getCurrentSchedulePrice(baseRow.price_schedule) : null;
}

// RRP is no longer its own typed-in value (story #146, dcn-domains-pricing-
// rrp-reference-price-merge) — it's derived from the Base UoM's own current
// Retail Price (vis_prices-and-pricelists.md). Gated by Company Settings'
// locked track_rrp: when on, the Base Price is shown converted to inc-GST
// and labeled RRP (what an RRP-tracking tenant means by the term); when
// off, the Base Price is shown as-is (ex-GST) and labeled Retail Price —
// no RRP concept is surfaced at all for that tenant. `value` is null when
// there's nothing to show (no Base Price set yet, or Track RRP is on but
// no default Tax Code exists to convert with).
export function getDerivedRrp(product, trackRrp, defaultTaxRate, anchorName = 'Retail') {
  const basePriceExGst = getBasePrice(product);
  if (!trackRrp) {
    return { heading: anchorName, gstSuffix: '(ex GST)', value: basePriceExGst };
  }
  const value = basePriceExGst !== null && defaultTaxRate !== null
    ? basePriceExGst * (1 + defaultTaxRate)
    : null;
  return { heading: anchorName, gstSuffix: '(inc GST)', value };
}

// Shared by every screen that shows the derived RRP/Retail Price (Product
// list, Product Card/Summary/Detail reports, UoM Retail Prices) so each
// one doesn't independently fetch the same two tenant-level settings.
// Company Settings 404s for a brand-new tenant that hasn't saved it yet —
// track_rrp defaults to true in that case, matching the model's own default
// (companySettings.js) rather than surfacing an error for an unsaved page.
export function useRrpSettings() {
  const [trackRrp, setTrackRrp] = useState(true);
  const [defaultTaxRate, setDefaultTaxRate] = useState(null);
  const anchorName = useAnchorPriceGroupName();

  useEffect(() => {
    axios.get('/api/company-settings')
      .then((res) => setTrackRrp(res.data.data?.track_rrp !== false))
      .catch(() => setTrackRrp(true));
    // Resolved backend-side (taxRateResolver.js) rather than re-deriving
    // "is_default" from the full list here — the same shared lookup the
    // emailed Pricelist PDF resolves through (pricelistPdfService.js),
    // so the Inc-GST figures on both documents can never drift apart.
    axios.get('/api/tax-codes/default')
      .then((res) => setDefaultTaxRate(res.data.data?.rate ?? null))
      .catch(() => setDefaultTaxRate(null));
  }, []);

  return { trackRrp, defaultTaxRate, anchorName };
}

export function sortedUomRows(product) {
  const baseRow = (product.uoms || []).find((r) => String(r.uom_id) === String(product.base_uom_id));
  const nonBaseRows = (product.uoms || [])
    .filter((r) => String(r.uom_id) !== String(product.base_uom_id))
    .sort((a, b) => Number(a.conversion_rate) - Number(b.conversion_rate));
  return [baseRow, ...nonBaseRows].filter(Boolean);
}

// Mirrors story #76's manual-price mechanic, now applied per dated schedule
// entry (issue #95): whether the *current* entry was manually typed in
// rather than auto-populated from the Base UoM's price. Unlike the single
// mutable price it replaces, is_manual is a real persisted flag per entry
// (set explicitly by the backend on add/edit/revert) rather than re-derived
// by comparison — the Base UoM's own row is never flagged as "manual"
// either way, since there's nothing to have auto-populated it from.
export function isManualPriceRow(uomRow, product) {
  if (String(uomRow.uom_id) === String(product.base_uom_id)) return false;
  const entry = getCurrentPriceEntry(uomRow.price_schedule);
  return !!entry?.is_manual;
}

// Single-product detail block — shared by the per-row Report view and the
// multi-product Detail Report so both stay pixel-identical. `nameFontSize`
// defaults to the single-product Report's heading size; the multi-product
// Detail Report passes a smaller value so repeated product names stay
// subordinate to that report's own title. `trackRrp`/`defaultTaxRate` come
// from useRrpSettings() in the caller (fetched once for the whole report
// rather than per product block).
export function ProductDetailBlock({ product, nameFontSize = 20, trackRrp, defaultTaxRate, anchorName }) {
  const rows = sortedUomRows(product);
  const hasManualPrice = rows.some((row) => isManualPriceRow(row, product));
  const rrp = getDerivedRrp(product, trackRrp, defaultTaxRate, anchorName);

  const metaParts = [
    product.product_category?.name,
    product.product_type ? `${product.product_type.code} — ${product.product_type.name}` : null,
    product.is_active ? 'Active' : 'Deactivated',
  ].filter(Boolean);

  return (
    <div>
      <div style={{ margin: '0 0 2px', fontSize: nameFontSize, fontWeight: 600 }}>{product.sku}</div>
      <h2 style={{ margin: '0 0 4px', fontSize: nameFontSize }}>{product.name}</h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#666', fontSize: 14, marginBottom: 16 }}>
        <span>{metaParts.join(' · ')}</span>
        <AvailabilityPill badge={product.availability_badge} date={product.availability_date} color={product.availability_badge_color} />
      </div>

      {product.description && (
        <div style={{ marginBottom: 16, fontSize: 14, fontWeight: 600 }}>{product.description}</div>
      )}

      <div style={{ display: 'flex', gap: 24, marginBottom: 16, fontSize: 14 }}>
        {product.supplier?.name && (
          <div>
            <div><strong>Supplier:</strong> {product.supplier.name}</div>
            {product.supplier_sku && <div style={{ color: '#666' }}>Supplier SKU: {product.supplier_sku}</div>}
            {product.purchase_description && <div style={{ color: '#666' }}>{product.purchase_description}</div>}
          </div>
        )}
        <div><strong>Cost:</strong> {formatCost(product)}</div>
        <div><strong>{rrp.heading} {rrp.gstSuffix}:</strong> {formatMoney(rrp.value)}</div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: reportStyle.catalogFontSize }}>
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: reportStyle.tableHeaderBorder }}>
            <th style={{ padding: '6px 8px 6px 0' }}>UoM</th>
            <th style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>Conversion Rate</th>
            <th style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>
              {/* Matches the row's price cell exactly (text + reserved
                  20px icon slot) so "Price" sits directly above the numbers
                  below it instead of flush against the cell's right edge
                  while the icon slot pushes the numbers left. */}
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                Price
                <span style={{ width: 20, flexShrink: 0 }} />
              </span>
            </th>
            <th style={{ padding: '6px 8px 6px 0' }}>UoM Control Type</th>
            <th style={{ padding: '6px 8px 6px 0', textAlign: 'center' }}>Can Sell</th>
            <th style={{ padding: '6px 8px 6px 0', textAlign: 'center' }}>Can Buy</th>
            <th style={{ padding: '6px 8px 6px 0', textAlign: 'center' }}>Tracked</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} style={{ borderBottom: '1px solid #ddd' }}>
              <td style={{ padding: '6px 8px 6px 0' }}>
                {row.uom?.code} — {row.uom?.name}
                {String(row.uom_id) === String(product.base_uom_id) ? ' (Base)' : ''}
              </td>
              <td style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>{formatNumber(row.conversion_rate, { maxDecimals: 4 })}</td>
              <td style={{ padding: '6px 8px 6px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline' }}>
                  <span>{formatMoney(getCurrentSchedulePrice(row.price_schedule))}</span>
                  <span style={{ width: 20, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                    {isManualPriceRow(row, product) && <ManualPriceIcon />}
                  </span>
                </div>
              </td>
              <td style={{ padding: '6px 8px 6px 0' }}>{row.uom_control_type ? `${row.uom_control_type.code} — ${row.uom_control_type.name}` : ''}</td>
              <td style={{ padding: '6px 8px 6px 0', textAlign: 'center' }}>{row.can_sell ? 'Y' : 'N'}</td>
              <td style={{ padding: '6px 8px 6px 0', textAlign: 'center' }}>{row.can_buy ? 'Y' : 'N'}</td>
              <td style={{ padding: '6px 8px 6px 0', textAlign: 'center' }}>{row.can_track ? 'Y' : 'N'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasManualPrice && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#888', marginTop: 4 }}>
          <ManualPriceIcon /> Price manually set — differs from conversion rate × Base UoM Price
        </div>
      )}
    </div>
  );
}
